import React, { useState } from "react";
import { availabilityApi, isAvailabilityConflict, apiErrorMessage } from "@/api/client";
import { Sheet } from "@/components/Sheet";
import { toast } from "sonner";

const PRESETS = {
  field: { full_day: true, start_time: "", end_time: "" },
  phone: { full_day: false, start_time: "09:00", end_time: "11:00" },
  email_desk: { full_day: false, start_time: "15:00", end_time: "17:00" },
};
const TITLE = { field: "Field day", phone: "Phone time", email_desk: "Email/Desk time" };
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function AvailabilityDialog({ open, onOpenChange, availabilityType, date, onSaved }) {
  const preset = PRESETS[availabilityType] || PRESETS.field;
  const [businessDate, setBusinessDate] = useState(date || new Date().toISOString().slice(0, 10));
  const [fullDay, setFullDay] = useState(preset.full_day);
  const [startTime, setStartTime] = useState(preset.start_time);
  const [endTime, setEndTime] = useState(preset.end_time);
  const [notes, setNotes] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [weekdays, setWeekdays] = useState([]);
  const [until, setUntil] = useState("");
  const [travelMode, setTravelMode] = useState("flexible");
  const [conflicts, setConflicts] = useState(null);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      const p = PRESETS[availabilityType] || PRESETS.field;
      setBusinessDate(date || new Date().toISOString().slice(0, 10));
      setFullDay(p.full_day); setStartTime(p.start_time); setEndTime(p.end_time);
      setNotes(""); setRecurring(false); setWeekdays([]); setUntil(""); setTravelMode("flexible"); setConflicts(null);
    }
  }, [open, availabilityType, date]);

  const toggleWeekday = (i) => setWeekdays((w) => w.includes(i) ? w.filter((x) => x !== i) : [...w, i]);

  const save = async (override = false) => {
    setSaving(true);
    const body = {
      availability_type: availabilityType,
      business_date: businessDate,
      full_day: fullDay,
      start_time: fullDay ? null : startTime,
      end_time: fullDay ? null : endTime,
      notes: notes || null,
      travel: { mode: travelMode },
      recurrence: recurring ? { enabled: true, weekdays, until: until || null } : null,
    };
    try {
      await availabilityApi.create(body, override);
      toast.success(`${TITLE[availabilityType]} added`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      if (isAvailabilityConflict(e)) {
        setConflicts(e.response.data.conflicts || []);
      } else {
        toast.error(apiErrorMessage(e));
      }
    } finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onClose={() => onOpenChange(false)} title={`Add ${TITLE[availabilityType]}`} testid="availability-dialog">
      <label className="mb-1 block text-xs font-semibold text-slate-600">Date</label>
      <input data-testid="avail-date-input" type="date" value={businessDate} onChange={(e) => setBusinessDate(e.target.value)}
        className="mb-3 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" />

      <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
        <input data-testid="avail-fullday-toggle" type="checkbox" checked={fullDay} onChange={(e) => setFullDay(e.target.checked)} className="h-4 w-4" />
        All day (London calendar day)
      </label>

      {!fullDay && (
        <div className="mb-3 grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Start</label>
            <input data-testid="avail-start-input" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
              className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">End</label>
            <input data-testid="avail-end-input" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
              className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" />
          </div>
        </div>
      )}

      <label className="mb-1 block text-xs font-semibold text-slate-600">Travel</label>
      <select data-testid="avail-travel-select" value={travelMode} onChange={(e) => setTravelMode(e.target.value)}
        className="mb-3 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm">
        <option value="flexible">Flexible (no anchor)</option>
        <option value="anchored">Anchored to home base</option>
      </select>

      <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
        <input data-testid="avail-recurring-toggle" type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} className="h-4 w-4" />
        Repeat weekly
      </label>
      {recurring && (
        <div className="mb-3">
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid="avail-weekdays">
            {WEEKDAYS.map((d, i) => (
              <button key={d} type="button" data-testid={`avail-weekday-${i}`} onClick={() => toggleWeekday(i)}
                className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${weekdays.includes(i) ? "border-teal-600 bg-teal-600 text-white" : "border-slate-200 text-slate-600"}`}>{d}</button>
            ))}
          </div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Until (optional)</label>
          <input data-testid="avail-until-input" type="date" value={until} onChange={(e) => setUntil(e.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" />
        </div>
      )}

      <label className="mb-1 block text-xs font-semibold text-slate-600">Notes (optional)</label>
      <input data-testid="avail-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
        className="mb-4 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" />

      {conflicts ? (
        <div data-testid="avail-conflict-warning" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          This overlaps {conflicts.length} existing block{conflicts.length === 1 ? "" : "s"}. Save anyway?
          <div className="mt-2 flex gap-2">
            <button data-testid="avail-override" onClick={() => save(true)} disabled={saving}
              className="min-h-[44px] flex-1 rounded-lg bg-amber-600 text-sm font-semibold text-white hover:bg-amber-700">Save anyway</button>
            <button onClick={() => setConflicts(null)} className="min-h-[44px] flex-1 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">Cancel</button>
          </div>
        </div>
      ) : (
        <button data-testid="avail-save" onClick={() => save(false)} disabled={saving}
          className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
          {saving ? "Saving…" : `Add ${TITLE[availabilityType]}`}
        </button>
      )}
    </Sheet>
  );
}
